import React from "react";
import {
  Drawer,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
} from "@mhome/ui";
import { useMediaQuery } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  backgroundTypeState,
  activeScopeState,
  safeAreaInsetsState,
} from "../global-state";
import {
  getBackground,
  useComponentBackgroundColor,
} from "../common/adaptive-theme-provider";
import {
  getChatHistory as getLocalChatHistory,
  clearChatHistory,
  getStorageUsage,
} from "../chat/chat-history-storage";

const ChatSettingDrawer = ({ open, onClose, onClearHistory }) => {
  const isSmallScreen = useMediaQuery("(max-width:768px)");
  const [activeScope] = useRecoilState(activeScopeState);
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);

  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();
  const backgroundType = useRecoilValue(backgroundTypeState);

  const handleLogHistory = async () => {
    if (activeScope) {
      try {
        const localHistory = await getLocalChatHistory(activeScope);
        const storageStats = await getStorageUsage();

        console.group("📋 Chat History & Storage Info");
        console.debug("Scope ID:", activeScope);
        console.debug("Chat History:", localHistory);
        console.debug("Total Chats:", localHistory.length);
        console.debug("Storage Stats:", storageStats);
        console.groupEnd();
      } catch (error) {
        console.error("Failed to retrieve chat history:", error);
      }
    } else {
      console.debug("No active scope found");
    }
  };

  const handleClearHistory = async () => {
    try {
      await clearChatHistory();
      console.debug("✅ Chat history cleared successfully");
      if (onClearHistory) {
        onClearHistory();
      }
    } catch (error) {
      console.error("❌ Failed to clear chat history:", error);
    }
  };

  const SettingsContent = () => {
    const [clearHovered, setClearHovered] = React.useState(false);
    const [logHovered, setLogHovered] = React.useState(false);

    return (
      <List style={{ padding: 0, width: "100%" }}>
        <ListItem style={{ padding: 0, width: "100%" }}>
          <ListItemButton
            onClick={handleClearHistory}
            style={{
              backgroundColor: clearHovered
                ? "hsl(var(--destructive) / 0.1)"
                : getComponentBgColor("hsl(var(--card))"),
              marginBottom: "8px",
              borderRadius: "16px",
              padding: "12px 16px",
              border: "none",
              display: "flex",
              alignItems: "center",
              transition: "background-color 0.2s",
              width: "100%",
            }}
            onMouseEnter={() => setClearHovered(true)}
            onMouseLeave={() => setClearHovered(false)}
          >
            <DeleteIcon
              style={{
                color: "hsl(var(--destructive))",
                fontSize: "20px",
                marginRight: "16px",
              }}
            />
            <ListItemText
              primary={
                <Typography
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: 400,
                    color: "hsl(var(--destructive))",
                  }}
                >
                  Clear History
                </Typography>
              }
              secondary={
                <Typography
                  variant="caption"
                  style={{
                    color: "hsl(var(--muted-foreground))",
                    fontSize: "0.75rem",
                  }}
                >
                  Remove all chat history from local storage
                </Typography>
              }
            />
          </ListItemButton>
        </ListItem>
        <ListItem style={{ padding: 0, width: "100%" }}>
          <ListItemButton
            onClick={handleLogHistory}
            style={{
              backgroundColor: logHovered
                ? "hsl(var(--primary) / 0.1)"
                : getComponentBgColor("hsl(var(--card))"),
              marginBottom: "8px",
              borderRadius: "16px",
              padding: "12px 16px",
              border: "none",
              display: "flex",
              alignItems: "center",
              transition: "background-color 0.2s",
              width: "100%",
            }}
            onMouseEnter={() => setLogHovered(true)}
            onMouseLeave={() => setLogHovered(false)}
          >
            <Typography
              component="span"
              style={{
                color: "hsl(var(--primary))",
                fontSize: "20px",
                marginRight: "16px",
                fontWeight: "bold",
              }}
            >
              📋
            </Typography>
            <ListItemText
              primary={
                <Typography
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: 400,
                    color: "hsl(var(--primary))",
                  }}
                >
                  Log
                </Typography>
              }
              secondary={
                <Typography
                  variant="caption"
                  style={{
                    color: "hsl(var(--muted-foreground))",
                    fontSize: "0.75rem",
                  }}
                >
                  Log chat history to console for debugging
                </Typography>
              }
            />
          </ListItemButton>
        </ListItem>
      </List>
    );
  };

  const ContentWrapper = () => (
    <div
      style={{
        width: "100%",
        height: "100%",
        paddingTop: `${safeAreaInsets.top}px`,
        display: "flex",
        flexDirection: "column",
        background: getBackground(backgroundType),
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px",
          borderBottom: "1px solid hsl(var(--border))",
        }}
      >
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
        <Typography variant="h6">Assistant Settings</Typography>
        <div style={{ width: 32 }} /> {/* Spacer to center the title */}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: "auto", padding: "16px" }}>
        <SettingsContent />
      </div>
    </div>
  );

  return isSmallScreen ? (
    <>
      {open && (
        <div
          role="presentation"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: parseInt(
              getComputedStyle(document.documentElement).getPropertyValue(
                "--z-index-drawer"
              ) || "1200",
              10
            ),
            background: getBackground(backgroundType),
          }}
        >
          <ContentWrapper />
        </div>
      )}
    </>
  ) : (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      style={{
        zIndex: parseInt(
          getComputedStyle(document.documentElement).getPropertyValue(
            "--z-index-drawer"
          ) || "1200",
          10
        ),
      }}
      PaperProps={{
        style: {
          width: 400,
        },
      }}
    >
      <ContentWrapper />
    </Drawer>
  );
};

export default ChatSettingDrawer;
